export const PUBLIC_SITE_ORIGIN = "https://autoplus.ps";

export type PublicRobots = "index,follow" | "noindex,follow";

export type PublicDocument = {
  status: 200 | 404;
  title: string;
  description: string;
  canonical: string;
  robots: PublicRobots;
};

export type CarDocumentFacts = {
  id: number;
  titleAr: string;
  year: number;
  location: string;
  fuelType: string;
  saleType: "auction" | "fixed" | string;
};

type PageCopy = { path: string; title: string; description: string };

export const INDEXABLE_PAGES: readonly PageCopy[] = [
  {
    path: "/",
    title: "سيارات للبيع في فلسطين | AutoPlus",
    description:
      "تصفّح سيارات للبيع في فلسطين عبر مزاد أو سعر ثابت. صور السيارة، السعر، والموقع قبل أن تتواصل مع البائع.",
  },
  {
    path: "/listings",
    title: "سيارات مستعملة للبيع في فلسطين | AutoPlus",
    description:
      "سيارات مستعملة للبيع في فلسطين على AutoPlus. قارن المزاد والسعر الثابت، ثم تواصل مع البائع.",
  },
  {
    path: "/about",
    title: "من نحن | AutoPlus",
    description: "AutoPlus منصة فلسطينية لبيع وشراء السيارات عبر المزادات والأسعار الثابتة.",
  },
  {
    path: "/faqs",
    title: "الأسئلة الشائعة | AutoPlus",
    description: "إجابات عن الشراء والبيع والمزاد وفحص السيارات على AutoPlus.",
  },
  {
    path: "/contact",
    title: "اتصل بنا | AutoPlus",
    description: "تواصل مع AutoPlus للاستفسار عن سيارة أو عن عرض سيارتك للبيع.",
  },
  {
    path: "/evaluation",
    title: "بيع سيارتي في فلسطين | AutoPlus",
    description: "اطلب تقييماً لسيارتك على AutoPlus، ثم اعرضها للبيع في فلسطين.",
  },
  {
    path: "/how-we-test",
    title: "كيف نفحص السيارات | AutoPlus",
    description: "كيف يفحص AutoPlus سيارة المزاد قبل اختيار حالتها.",
  },
  {
    path: "/test-points",
    title: "نقاط الفحص | AutoPlus",
    description: "نقاط الفحص التي يراجعها AutoPlus على سيارة المزاد.",
  },
  {
    path: "/car-condition",
    title: "حالة السيارة | AutoPlus",
    description: "حالات سيارات المزاد على AutoPlus: معتمد بلس، معتمد، مفحوص، وبحاجة لإصلاح.",
  },
  {
    path: "/help",
    title: "المساعدة | AutoPlus",
    description: "مساعدة استخدام AutoPlus للبحث عن سيارة أو متابعة إعلان.",
  },
  {
    path: "/support",
    title: "الدعم | AutoPlus",
    description: "دعم AutoPlus للحسابات والإعلانات والمزادات.",
  },
  {
    path: "/privacy",
    title: "سياسة الخصوصية | AutoPlus",
    description: "سياسة الخصوصية لمنصة AutoPlus.",
  },
  {
    path: "/terms",
    title: "شروط الاستخدام | AutoPlus",
    description: "شروط استخدام منصة AutoPlus.",
  },
  {
    path: "/cookies",
    title: "سياسة ملفات تعريف الارتباط | AutoPlus",
    description: "كيف تستخدم AutoPlus ملفات تعريف الارتباط.",
  },
];

const INDEXABLE = new Map(INDEXABLE_PAGES.map((page) => [page.path, page]));

const APP_PREFIXES = [
  "/login",
  "/register",
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/add-car",
  "/edit-car",
  "/profile",
  "/favorites",
  "/status",
  "/404",
  "/dashboard",
  "/dealership",
];

const NOT_FOUND: PublicDocument = {
  status: 404,
  title: "الصفحة غير موجودة | AutoPlus",
  description: "هذا العنوان غير موجود على AutoPlus.",
  canonical: `${PUBLIC_SITE_ORIGIN}/404`,
  robots: "noindex,follow",
};

export function normalizePublicPath(pathname: string): string {
  const path = pathname.split("?")[0]?.split("#")[0] || "/";
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
}

export function carIdFromPath(pathname: string): number | null {
  const match = normalizePublicPath(pathname).match(/^\/car\/(\d+)$/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function appDocument(pathname: string): PublicDocument {
  return {
    status: 200,
    title: "AutoPlus",
    description: "AutoPlus منصة بيع وشراء السيارات عبر المزادات والأسعار الثابتة.",
    canonical: `${PUBLIC_SITE_ORIGIN}${pathname}`,
    robots: "noindex,follow",
  };
}

export function carPublicDocument(facts: CarDocumentFacts): PublicDocument {
  const place = facts.location.trim() || "فلسطين";
  const fuel = facts.fuelType.trim();
  const sale = facts.saleType === "auction" ? "مزاد" : "سعر ثابت";
  const name = facts.titleAr.trim();
  const details = [name, String(facts.year), fuel, sale].filter(Boolean).join("، ");
  const description = `${details}، في ${place}. شاهد الصور وتواصل مع البائع عبر AutoPlus.`;
  return {
    status: 200,
    title: `${name} ${facts.year} للبيع في ${place} | AutoPlus`,
    description: description.length > 160 ? `${description.slice(0, 157)}...` : description,
    canonical: `${PUBLIC_SITE_ORIGIN}/car/${facts.id}`,
    robots: "index,follow",
  };
}

/**
 * Document for a public path.
 * For /car/:id, pass the car, null when it is confirmed missing, or "unknown" when lookup failed.
 */
export function resolvePublicDocument(
  pathname: string,
  car?: CarDocumentFacts | null | "unknown",
): PublicDocument {
  const path = normalizePublicPath(pathname);
  const page = INDEXABLE.get(path);
  if (page) {
    return {
      status: 200,
      title: page.title,
      description: page.description,
      canonical: `${PUBLIC_SITE_ORIGIN}${page.path === "/" ? "/" : page.path}`,
      robots: "index,follow",
    };
  }

  if (path.startsWith("/car/")) {
    if (!carIdFromPath(path)) return NOT_FOUND;
    if (car === "unknown" || car === undefined) return appDocument(path);
    if (car === null) return NOT_FOUND;
    return carPublicDocument(car);
  }

  if (APP_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return appDocument(path);
  }

  return NOT_FOUND;
}

export function sitemapPaths(carIds: readonly number[]): string[] {
  const paths = INDEXABLE_PAGES.map((page) => page.path);
  for (const id of carIds) {
    if (Number.isSafeInteger(id) && id > 0) paths.push(`/car/${id}`);
  }
  return paths;
}

export function buildSitemapXml(carIds: readonly number[]): string {
  const body = sitemapPaths(carIds)
    .map((path) => `  <url><loc>${PUBLIC_SITE_ORIGIN}${path === "/" ? "/" : path}</loc></url>`)
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
}

function attr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function replaceMeta(html: string, attrName: string, key: string, content: string): string {
  const pattern = new RegExp(`(${attrName}=["']${key}["']\\s+content=["'])[^"']*(["'])`);
  const alt = new RegExp(`(content=["'])[^"']*(["']\\s+${attrName}=["']${key}["'])`);
  if (pattern.test(html)) return html.replace(pattern, `$1${attr(content)}$2`);
  if (alt.test(html)) return html.replace(alt, `$1${attr(content)}$2`);
  return html;
}

export function injectPublicDocument(html: string, doc: PublicDocument): string {
  let out = html.replace(/<title>[\s\S]*?<\/title>/, `<title>${attr(doc.title)}</title>`);
  out = replaceMeta(out, "name", "description", doc.description);
  out = replaceMeta(out, "property", "og:title", doc.title);
  out = replaceMeta(out, "property", "og:description", doc.description);
  out = replaceMeta(out, "property", "og:url", doc.canonical);
  out = replaceMeta(out, "name", "twitter:title", doc.title);
  out = replaceMeta(out, "name", "twitter:description", doc.description);
  out = out.replace(/rel="canonical" href="[^"]*"/, `rel="canonical" href="${attr(doc.canonical)}"`);
  if (out.includes('name="robots"')) {
    out = replaceMeta(out, "name", "robots", doc.robots);
  } else {
    out = out.replace("</head>", `    <meta name="robots" content="${doc.robots}" />\n  </head>`);
  }
  return out;
}
